import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { expertService } from "@/services/expertService";
import { apiError } from "@/lib/api";
import { Volume2, Loader2, Plus, Check, GraduationCap, Award, BookOpen, Trash2 } from "lucide-react";

const SCORES = [
  { key: "peer_recognition", label: "Peer Recognition" },
  { key: "publication_depth", label: "Publication Depth" },
  { key: "practical_experience", label: "Practical Experience" },
];

function CredibilityBar({ label, value }) {
  return (
    <div data-testid={`cred-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] uppercase tracking-wider text-white/50 font-semibold">{label}</span>
        <span className="relative text-xs font-mono font-bold text-emerald-300 tabular-nums">
          {value}
          {/* Verification Pulse — signals live AI credibility calc */}
          <span className="absolute -right-2.5 top-1/2 -translate-y-1/2 flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
          </span>
        </span>
      </div>
      <div className="h-1.5 w-full bg-white/10 overflow-hidden rounded-full">
        <motion.div
          className="h-full bg-gradient-to-r from-emerald-500 to-emerald-300"
          initial={{ width: 0 }} animate={{ width: `${value}%` }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
    </div>
  );
}

export function ExpertCard({ expert, topic, index = 0, isMember, collected, onLocked, onCollected, onRemove }) {
  const audioRef = useRef(null);
  const [narrating, setNarrating] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(collected);
  const [generating, setGenerating] = useState(false);

  const playSummary = async () => {
    if (playing && audioRef.current) { audioRef.current.pause(); setPlaying(false); return; }
    setNarrating(true);
    try {
      const b64 = await expertService.narrate(expert.summary || expert.one_line || expert.name);
      const bin = atob(b64); const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: "audio/mpeg" }));
      if (!audioRef.current) audioRef.current = new Audio();
      audioRef.current.src = url;
      audioRef.current.onended = () => setPlaying(false);
      await audioRef.current.play(); setPlaying(true);
    } catch (e) { toast.error(apiError(e)); }
    finally { setNarrating(false); }
  };

  const collect = async () => {
    if (!isMember) { onLocked?.(); return; }
    setSaving(true);
    try {
      await expertService.collect(expert, topic);
      setSaved(true); onCollected?.(expert);
      toast.success(`${expert.name} added to your Experts List`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const becomeExpert = async () => {
    if (!isMember) { onLocked?.(); return; }
    setGenerating(true);
    try {
      const course = await expertService.generateCourse(expert, topic);
      toast.success("Your expert deep-dive is ready!");
      window.location.assign(`/course/${course.id}`);
    } catch (e) { toast.error(apiError(e)); setGenerating(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: index * 0.06 }}
      className="flex flex-col bg-[#0d1117] border border-white/10 hover:border-emerald-500/40 transition-colors p-6"
      data-testid={`expert-card-${expert.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="min-w-0">
          <h3 className="font-display text-lg font-extrabold text-white leading-tight truncate">{expert.name}</h3>
          <p className="text-xs text-emerald-300/80 font-semibold truncate">{expert.title}</p>
        </div>
        <button onClick={playSummary} disabled={narrating}
          title="Play narrated summary" data-testid={`expert-narrate-${expert.id}`}
          className={`shrink-0 grid place-items-center h-9 w-9 rounded-full border transition-colors ${playing ? "border-emerald-400 text-emerald-300 bg-emerald-500/15" : "border-white/20 text-white/70 hover:text-white hover:border-white/50"}`}>
          {narrating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume2 className="h-4 w-4" />}
        </button>
      </div>

      <p className="text-sm text-white/70 leading-relaxed mt-2 mb-4">{expert.summary || expert.one_line}</p>

      <div className="space-y-3 mb-4">
        {SCORES.map((s) => (
          <CredibilityBar key={s.key} label={s.label} value={expert.credibility?.[s.key] ?? 0} />
        ))}
        <p className="text-[10px] text-white/35 italic">Credibility scores are AI-estimated.</p>
      </div>

      {expert.notable_works?.length > 0 && (
        <div className="mb-3">
          <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-white/50 font-semibold mb-1"><BookOpen className="h-3 w-3" /> Notable works</p>
          <ul className="text-xs text-white/70 space-y-0.5 list-disc list-inside">
            {expert.notable_works.slice(0, 4).map((w, i) => <li key={i} className="truncate">{w}</li>)}
          </ul>
        </div>
      )}
      {expert.credentials?.length > 0 && (
        <div className="mb-4">
          <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-white/50 font-semibold mb-1"><Award className="h-3 w-3" /> Credentials</p>
          <div className="flex flex-wrap gap-1.5">
            {expert.credentials.slice(0, 4).map((c, i) => (
              <span key={i} className="text-[11px] text-white/70 border border-white/15 px-2 py-0.5">{c}</span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-auto flex flex-col gap-2 pt-3 border-t border-white/10">
        {onRemove ? (
          <button onClick={() => onRemove(expert)} data-testid={`expert-remove-${expert.id}`}
            className="inline-flex items-center justify-center gap-2 h-9 border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 transition-colors text-sm font-semibold">
            <Trash2 className="h-4 w-4" /> Remove from list
          </button>
        ) : (
          <button onClick={collect} disabled={saving || saved} data-testid={`expert-collect-${expert.id}`}
            className={`inline-flex items-center justify-center gap-2 h-9 border transition-colors text-sm font-semibold ${saved ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10 cursor-default" : "border-white/20 text-white hover:border-emerald-400 hover:text-emerald-300"} ${!isMember ? "opacity-90" : ""}`}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : saved ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {saved ? "In your Experts List" : "To Experts List"}
          </button>
        )}
        <button onClick={becomeExpert} disabled={generating} data-testid={`expert-become-${expert.id}`}
          className="inline-flex items-center justify-center gap-2 h-10 bg-emerald-500 text-[#04110b] hover:bg-emerald-400 transition-colors text-sm font-bold">
          {generating ? <><Loader2 className="h-4 w-4 animate-spin" /> Building deep-dive…</> : <><GraduationCap className="h-4 w-4" /> Become an Expert on the Expert</>}
        </button>
      </div>
    </motion.div>
  );
}
